import { API_BASE_URL } from "../config";

export class ApiError extends Error {
    constructor({ status, code, message, details }) {
        super(message || code || `HTTP ${status}`);
        this.name = "ApiError";
        this.status = status;
        this.code = code || null;
        this.details = details || null;
    }
}

async function parseError(res) {
    const body = await res.json().catch(() => null);
    return new ApiError({
        status: res.status,
        code: body?.error,
        message: body?.message || `Request failed (HTTP ${res.status})`,
        details: body?.details,
    });
}

// Global handler for account_disabled — set from AuthContext on mount so
// every API call that hits a suspended session can short-circuit the UI
// out of the protected app and into /account-suspended.
let onAccountDisabled = null;
export function setAccountDisabledHandler(fn) {
    onAccountDisabled = fn;
}

async function request(path, { method = "GET", body, signal } = {}) {
    const res = await fetch(`${API_BASE_URL}${path}`, {
        method,
        credentials: "include",
        headers: body ? { "Content-Type": "application/json" } : {},
        body: body ? JSON.stringify(body) : undefined,
        signal,
    });
    if (!res.ok) {
        const err = await parseError(res);
        if (err.code === "account_disabled" && onAccountDisabled) {
            try {
                onAccountDisabled();
            } catch {
                /* swallow */
            }
        }
        throw err;
    }
    if (res.status === 204) return null;
    return res.json();
}

function buildQuery(params) {
    if (!params) return "";
    const parts = Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null && v !== "")
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
    return parts.length ? `?${parts.join("&")}` : "";
}

export function sendFeedback({ message, page }, opts = {}) {
    return request("/feedback", { method: "POST", body: { message, page }, ...opts });
}

export function assistantStatus(opts = {}) {
    return request("/assistant/status", { ...opts });
}

export function assistantChat({ message, history, page }, opts = {}) {
    return request("/assistant/chat", {
        method: "POST",
        body: { message, history, page },
        ...opts,
    });
}

export function packSingle({ bin, box }, opts = {}) {
    return request("/pack", { method: "POST", body: { bin, box }, ...opts });
}

export function packMulti({ bin, boxes }, opts = {}) {
    return request("/pack/multi", { method: "POST", body: { bin, boxes }, ...opts });
}

export function listSkus(opts = {}) {
    return request("/skus", { method: "GET", ...opts });
}

export function createSku(payload, opts = {}) {
    return request("/skus", { method: "POST", body: payload, ...opts });
}

export function updateSku(id, patch, opts = {}) {
    return request(`/skus/${id}`, { method: "PUT", body: patch, ...opts });
}

export function deleteSku(id, opts = {}) {
    return request(`/skus/${id}`, { method: "DELETE", ...opts });
}

export function authRegister(body, opts = {}) {
    return request("/auth/register", { method: "POST", body, ...opts });
}

export function authLogin(body, opts = {}) {
    return request("/auth/login", { method: "POST", body, ...opts });
}

export function authVerify(token, opts = {}) {
    return request("/auth/verify", { method: "POST", body: { token }, ...opts });
}

export function authResendVerification(email, opts = {}) {
    return request("/auth/resend-verification", {
        method: "POST",
        body: { email },
        ...opts,
    });
}

export function authForgotPassword(email, opts = {}) {
    return request("/auth/forgot-password", {
        method: "POST",
        body: { email },
        ...opts,
    });
}

export function authResetPassword(token, password, opts = {}) {
    return request("/auth/reset-password", {
        method: "POST",
        body: { token, password },
        ...opts,
    });
}

export function authMagicLink(email, opts = {}) {
    return request("/auth/magic-link", {
        method: "POST",
        body: { email },
        ...opts,
    });
}

export function authMagicLogin(token, opts = {}) {
    return request("/auth/magic-login", {
        method: "POST",
        body: { token },
        ...opts,
    });
}

export const admin = {
    listUsers: (filters, opts = {}) =>
        request(`/admin/users${buildQuery(filters)}`, { method: "GET", ...opts }),
    getUser: (id, opts = {}) =>
        request(`/admin/users/${id}`, { method: "GET", ...opts }),
    listUserEvents: (id, params, opts = {}) =>
        request(`/admin/users/${id}/events${buildQuery(params)}`, {
            method: "GET",
            ...opts,
        }),
    listUserSkus: (id, opts = {}) =>
        request(`/admin/users/${id}/skus`, { method: "GET", ...opts }),
    listUserPacks: (id, params, opts = {}) =>
        request(`/admin/users/${id}/packs${buildQuery(params)}`, {
            method: "GET",
            ...opts,
        }),
    listEvents: (filters, opts = {}) =>
        request(`/admin/events${buildQuery(filters)}`, { method: "GET", ...opts }),
    getPack: (id, opts = {}) =>
        request(`/admin/packs/${id}`, { method: "GET", ...opts }),
    stats: (opts = {}) => request(`/admin/stats`, { method: "GET", ...opts }),

    verifyEmail: (id, opts = {}) =>
        request(`/admin/users/${id}/verify-email`, { method: "POST", ...opts }),
    sendPasswordReset: (id, opts = {}) =>
        request(`/admin/users/${id}/send-password-reset`, {
            method: "POST",
            ...opts,
        }),
    sendMagicLink: (id, opts = {}) =>
        request(`/admin/users/${id}/send-magic-link`, {
            method: "POST",
            ...opts,
        }),
    disableUser: (id, reason, opts = {}) =>
        request(`/admin/users/${id}/disable`, {
            method: "POST",
            body: { reason },
            ...opts,
        }),
    enableUser: (id, reason, opts = {}) =>
        request(`/admin/users/${id}/enable`, {
            method: "POST",
            body: { reason },
            ...opts,
        }),
    promoteUser: (id, reason, opts = {}) =>
        request(`/admin/users/${id}/promote`, {
            method: "POST",
            body: { reason },
            ...opts,
        }),
    demoteUser: (id, reason, opts = {}) =>
        request(`/admin/users/${id}/demote`, {
            method: "POST",
            body: { reason },
            ...opts,
        }),
    setUserTier: (id, tier, reason, opts = {}) =>
        request(`/admin/users/${id}/tier`, {
            method: "POST",
            body: { tier, reason },
            ...opts,
        }),
    anonymiseUser: (id, reason, opts = {}) =>
        request(`/admin/users/${id}`, {
            method: "DELETE",
            body: { reason },
            ...opts,
        }),
    deleteSku: (id, reason, opts = {}) =>
        request(`/admin/skus/${id}`, {
            method: "DELETE",
            body: { reason },
            ...opts,
        }),
};
